import { Component, EventEmitter, HostListener, Input, OnInit, Output } from "@angular/core";
import { InspectionApiService } from "../../services/inspection-api.service";
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { FormUtils } from "../../../forms/forms";
import { InspectionDefinition, InspectionInput, InspectionStep, InspectionType } from "../../models/inspection-definition.model";
import { Observable } from "rxjs";
import { EnvironmentConfig, GuidUtils, LogService, ModalService, ShareService, VideoCaptureComponent, WindowService } from "../../../core/core";
import { ToastrService } from "ngx-toastr";
import { ActivatedRoute, Router } from "@angular/router";
import { generate, observe } from "fast-json-patch";

declare const environment: EnvironmentConfig;
@Component({
  selector: "common-inspection-form",
  templateUrl: "./inspection-form.component.html",
  styleUrls: ["./inspection-form.component.scss"],
})
export class InspectionFormComponent implements OnInit {
  @Input() inspection: InspectionDefinition;
  @Output() inspectionChange = new EventEmitter<InspectionDefinition>();
  @Output() inspectionSubmitted = new EventEmitter<boolean>();

  loaded: boolean = false;
  disableButton: boolean = false;
  formSubmitted: boolean = false;
  title: string;
  persona: string;
  inspectionDefinitionId: string;
  inspectionDefinition: InspectionDefinition;
  rentalItemStateId: string;
  inventoryItemId: string;
  inspectionType: InspectionType;
  inspectionForm: FormGroup;
  formUtils = FormUtils;
  errorMessage: string;
  errorStack: string;
  currentSectionIndex: number = null;
  draftId: string;
  triedSubmitting: boolean;

  // can only deactivate if form is submitted
  @HostListener("window:beforeunload", ["$event"]) canDeactivate(): Observable<boolean> | boolean {
    if (environment.production === "false") {
      return true;
    }

    return this.formSubmitted;
  }

  constructor(
    private formBuilder: FormBuilder,
    private toastrService: ToastrService,
    private router: Router,
    private route: ActivatedRoute,
    private windowService: WindowService,
    private logService: LogService,
    private shareService: ShareService,
    private modalService: ModalService,
    private inspectionApiService: InspectionApiService,
  ) {}

  ngOnInit(): void {
    this.setupForm(this.inspection);
  }

  setupForm(definition: InspectionDefinition) {
    // need to set index of each section
    definition.sections.forEach((section, i) => {
      section.index = i;
      section.complete = section.internalNotes?.includes("complete") || false;
      section.inProgress = section.internalNotes?.includes("inProgress") || false;
    });

    this.inspectionDefinition = definition;
    try {
      this.inspectionForm = this.formBuilder.group({
        sections: this.formBuilder.array([]),
        startTime: [new Date()],
        inspectionType: definition.inspectionType,
        progress: "Incomplete",
        externalType: this.title || definition.inspectionType,
        endTime: [],
      });

      const sectionsFA = this.inspectionForm.get("sections") as FormArray;
      // iterate through all the sections. create a form group
      for (let [sectionIndex, section] of definition.sections.entries()) {
        const inspectionStepsFa = this.formBuilder.array([]);
        // start of inspection steps
        section.jsonPatchPath = `/sections/${sectionIndex}`;
        for (let [stepIndex, step] of section.inspectionSteps.entries()) {
          step.id = GuidUtils.createGuid();
          step.jsonPatchPath = `/sections/${sectionIndex}/inspectionSteps/${stepIndex}`;

          if ((step.stepType === "BoolStep" && !step.positiveLabel) || !step.negativeLabel) {
            // if there is no label we can not identify which is the correct selected answer
            // thats why we do this before create the formbuilder group
            step.positiveLabel = "Pass";
            step.negativeLabel = "Fail";
          }

          // create step form group
          let stepFG = this.formBuilder.group(step);
          switch (step.stepType) {
            case "DataCollection":
              break;

            case "BoolStep":
              // in the future we always want to require answer. this is just a one off cause we hacked in attachments
              if (!section.title.toLowerCase().startsWith("attachment")) {
                if (!stepFG.get("internalNotes").value?.includes("touched")) {
                  stepFG.get("answer").setValue(null);
                }

                stepFG.get("answer").setValidators(Validators.required);
              } else {
                stepFG.get("answer").setValue(true);
                console.log("REMOVE THIS logic for attachments");
              }
              break;

            case "MultiAcknowledgementStep":
              const maFA = this.formBuilder.array([]);
              for (let ack of step.acknowledgements) {
                let ackFG = this.formBuilder.group(ack);
                ackFG.get("answer").setValidators(Validators.requiredTrue);
                maFA.push(ackFG);
              }
              stepFG.controls["acknowledgements"] = maFA;

              break;

            case "VerificationStep":
              stepFG.get("value").setValidators(Validators.required);
              break;
          } // end step type

          // start of step inputs
          const inputsFA = this.formBuilder.array([]);
          for (let [inputIndex, input] of step.inputs.entries()) {
            // before creating a form group we need to set up certain properties
            input.id = GuidUtils.createGuid();
            input.jsonPatchPath = `/sections/${sectionIndex}/inspectionSteps/${stepIndex}/inputs/${inputIndex}`;
            input.stepId = step.id; // assign the corresponding step to this input
            input.dropdownItems = input.dropdownItems?.length ? input.dropdownItems : [];
            input.expectedImageOrientation = null;
            input.expectedImageOrientationMessage = null;

            let inputFG = this.formBuilder.group(input);

            switch (input.inputType) {
              case "Photo":
                if (input.requirement === "Always") {
                  inputFG.get("storedImage").setValidators(Validators.required);
                }

                // if we have listing photo we need to set the expected image orientation
                if (step?.title?.toLowerCase()?.includes("listing photo") || step?.helpText?.toLowerCase()?.includes("landscape")) {
                  inputFG.get("expectedImageOrientation").setValue("Landscape");
                  inputFG
                    .get("expectedImageOrientationMessage")
                    .setValue("Landscape(horizontal) photo is required for listing photos. Please step back and rotate your phone while taking the photo.");
                }
                break;
              case "Video":
                if (input.requirement === "Always") {
                  inputFG.get("videoStorage").setValidators(Validators.required);
                }
                break;

              case "Barcode":
                if (input.requirement === "Always") {
                  inputFG.get("value").setValidators(Validators.required);
                }
                break;

              case "Text":
                if (input.requirement === "Always") {
                  inputFG.get("value").setValidators(Validators.required);
                }
                break;

              case "Dropdown":
                // if we have dropdown items already no need to remap. this is for draft mode
                if (!input.dropdownItems.length) {
                  input.dropdownItems = input.items.map(item => {
                    return { value: item.key, label: item.value };
                  });
                }

                inputFG.get("dropdownItems").setValue(input.dropdownItems);

                // find if we have selected dropdown
                const selectedDropdown = input.dropdownItems.find(d => d.value == input.value);
                this.dropdownSelected(inputFG, selectedDropdown?.value || input.dropdownItems[0].value, false);

                break;
            }

            inputsFA.push(inputFG);
          }
          // end of inputs
          stepFG.controls["inputs"] = inputsFA;
          inspectionStepsFa.push(stepFG);
        } // end of steps

        const sectionFormGroup = this.formBuilder.group(section) as FormGroup;
        sectionFormGroup.controls["inspectionSteps"] = inspectionStepsFa;
        sectionsFA.push(sectionFormGroup);
      } // end of section

      this.currentSectionIndex = definition.currentSectionIndex;
      this.loaded = true;

      console.log(definition);
      console.log(this.inspectionForm.value);
    } catch (error) {
      this.onError(error, "Unable to set up inspection form");
    }
  }

  checkboxChange(value: boolean, formControl: AbstractControl) {
    // grab all inputs for the correpsonding form conrol
    const inputs = formControl.get("inputs");
    // filter all inputs for optional fail
    // if fail set the validators on the control to required. other wise clear validators
    for (let input of inputs["controls"].filter(d => d.value.requirement === "OptionalFailRequired")) {
      let control = null;
      switch (input.value.inputType) {
        case "Photo":
          control = input.get("storedImage");
          if (control) {
            value === false ? control.setValidators(Validators.required) : control.clearValidators();
          }
          break;

        case "Text":
          control = input.get("value");
          if (control) {
            value === false ? control.setValidators(Validators.required) : control.clearValidators();
          }
          break;

        default:
          console.error("checkboxChange: could not find input to change validator requirements");
          break;
      }
    }
    this.formUtils.updateValueAndValidity(this.inspectionForm);
    // re enable the button always
    this.disableButton = false;
    this.jsonSaveDraft(formControl.value);
  }

  acknowledgementChange(stepControl: any) {
    this.formUtils.updateValueAndValidity(this.inspectionForm);
    this.jsonSaveDraft(stepControl.value);
  }

  jsonSaveDraft(inspectionStep: InspectionStep) {
    this.inspectionForm.value.currentSectionIndex = this.currentSectionIndex;
    this.formUtils.updateValueAndValidity(this.inspectionForm);
    inspectionStep.internalNotes = "touched";

    var operation = [{ op: "replace", path: inspectionStep.jsonPatchPath, value: inspectionStep }];
    this.inspectionApiService.jsonPatchWithPartitionKey(this.inspection.id, operation, this.inspection.partitionKey).subscribe(result => {});
    this.inspectionChange.emit(this.inspectionForm.value);
  }

  recordVideo(inputFormControl: AbstractControl) {
    const modalRef = this.modalService.standardModal(VideoCaptureComponent, false);
    modalRef.result.then(
      (recordedBlob: Blob) => {
        this.inspectionApiService
          .uploadMedia(this.inspection.id, inputFormControl.value.stepId, inputFormControl.value.id, null, recordedBlob, 0)
          .subscribe(result => {
            inputFormControl.get("videoStorage").patchValue(result);
            inputFormControl.updateValueAndValidity();
            this.jsonSaveDraft(inputFormControl.value);
          });
      },
      cancel => {},
    );
  }

  // this is for integration tests since the integration tests dont have a webcam
  uploadFakeVideo(event: any, formControl: AbstractControl) {
    const files = event.target.files;
    formControl.patchValue(files[0]);

    this.inspectionApiService.uploadMedia(this.inspection.id, formControl.value.stepId, formControl.value.id, null, files[0], 0).subscribe(result => {
      formControl.get("videoStorage").patchValue(result);
      formControl.updateValueAndValidity();
      this.jsonSaveDraft(formControl.value);
    });
  }

  deleteVideo(formControl: AbstractControl) {
    formControl.get("videoStorage").patchValue(null);
    this.jsonSaveDraft(formControl.value);
  }

  showInput(step: InspectionStep, inputFG: FormGroup): boolean {
    if (["Always"].includes(inputFG.value.requirement)) {
      return true;
    }

    if (step.stepType == "DataCollection") {
      return true;
    }

    if (inputFG.value.inputType == "Dropdown") {
      return; // we dont do anything for dropdown yet
    }

    // the input requirement is FailOnly. so we need to dynamically set the validators to required if it is a failuire
    const inputMappings = {
      Photo: inputFG.get("storedImage"),
      Video: inputFG.get("videoStorage"),
      Barcode: inputFG.get("value"),
      Text: inputFG.get("value"),
    };

    const inputValueForValidator = inputMappings[inputFG.value.inputType] as FormGroup;
    if (!inputValueForValidator) {
      this.logService.error(this.constructor.name, new Error("need to add more input groupings to the inputMappings"));
      return;
    }

    if ((inputFG.value.inputType == "Video" || inputFG.value.inputType == "Photo") && inputFG.value.requirement == "Optional") {
      return true;
    }

    if (step.answer === false) {
      inputValueForValidator.setValidators(Validators.required);
      inputValueForValidator.updateValueAndValidity();
      return true;
    }

    inputValueForValidator.clearValidators();
    inputValueForValidator.updateValueAndValidity();
    return false;
  }

  photoSelected(formControl: any, imageBlob: any) {
    if (imageBlob == null) {
      formControl.get("storedImage").patchValue(null);
      this.jsonSaveDraft(formControl.value);
      return;
    }

    this.inspectionApiService.uploadMedia(this.inspection.id, formControl.value.stepId, formControl.value.id, imageBlob, null, 0).subscribe(result => {
      formControl.get("storedImage").patchValue(result);
      formControl.updateValueAndValidity();
      this.jsonSaveDraft(formControl.value);
    });
    this.disableButton = false;
  }

  barcodeValueSelected(formControl: AbstractControl, value: any) {
    formControl.patchValue(value);
    this.disableButton = false;
  }

  inputsToVerify = [
    {
      instruction: "Rubbl Id",
      queryString: "rubblId",
      warningMessage: expectedValue =>
        `The Rubbl Asset Id entered does not match the expected value. Please make sure you have entered it correctly and are inspecting the correct machine for this order: ${expectedValue}\n\nContact Rubbl support at (866) 455-8531.`,
    },
  ];

  verifyInputBlur(formControlValue: any, value: any) {
    this.jsonSaveDraft(formControlValue);
    if (!value) {
      // user has not entered anything
      return;
    }

    const correspondingVerification = this.inputsToVerify.find(d => d.instruction === formControlValue.instruction);
    if (!correspondingVerification) {
      // didnt find an verfication for this input
      return;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const correspondingQueryString = urlParams.get(correspondingVerification.queryString);
    if (!correspondingQueryString) {
      // didnt find a query string with an expected value
      return;
    }

    if (correspondingQueryString === value) {
      // value the user entered matches the expected value
      return;
    }

    window.alert(correspondingVerification.warningMessage(correspondingQueryString));
  }

  dropdownSelected(input: AbstractControl, value: any, saveDraft: boolean = true) {
    const selectedDropdownItem = input.value.dropdownItems.find(d => d.value == value);
    if (selectedDropdownItem) {
      input.get("items").setValue([{ key: selectedDropdownItem.value, value: selectedDropdownItem.label }]);
    }

    input.get("value").setValue(value);

    if (saveDraft) {
      this.jsonSaveDraft(input.value); // is this right?
    }
  }

  onSubmit() {
    this.logService.info(this.constructor.name, "onSubmit start");
    try {
      this.formUtils.updateValueAndValidity(this.inspectionForm);
      const inspection = this.inspectionForm.value as InspectionDefinition;
      const sectionsNotComplete = inspection.sections.filter(d => !d.complete);
      // we need it to be greater then 1 because the last section is technically not complete

      if (this.inspectionForm.invalid) {
        console.log(this.inspectionForm);
        FormUtils.markFormGroup(this.inspectionForm);
        this.toastrService.error("errors found", null, {
          positionClass: "toast-top-center",
        });

        // reason for timeout is because it took angular some time to show error text. we can probably do this better
        setTimeout(() => {
          this.windowService.scrollToElement(".form-text,.text-red");
        }, 300);

        return;
      }

      this.inspectionForm.patchValue({ endTime: new Date(), progress: "Complete" });
      this.disableButton = true;

      // need to json patch the last section and update status
      const operation = [
        { op: "replace", path: "/status", value: "Complete" },
        { op: "replace", path: "/endTime", value: new Date() },
        { op: "replace", path: `/sections/${this.currentSectionIndex}/internalNotes`, value: "complete" },
      ];

      this.inspectionApiService.jsonPatchWithPartitionKey(this.inspection.id, operation, this.inspection.partitionKey).subscribe(result => {
        this.logService.trackEvent("inspectionCreateComplete", { event: "inspectionCreateComplete", inspectionType: this.inspectionType }, true);
        this.formSubmitted = true;
        this.toastrService.success("Inspection submitted", null, {
          positionClass: "toast-top-center",
        });
        this.inspectionSubmitted.emit();
      });
    } catch (error) {
      this.onError(error, "Unable to submit inspection");
    }
  }

  onError(error, errorMessage) {
    this.logService.info(this.constructor.name, `onError start`);
    this.errorMessage = errorMessage;
    this.errorStack = error.stack;
    this.disableButton = false;
    this.logService.error(this.constructor.name, error);
    setTimeout(() => {
      this.windowService.scrollToElement("#error");
    }, 250);
  }

  shareError() {
    this.shareService.share(this.errorMessage, this.errorStack, window.location.href);
  }

  get sections() {
    return this.inspectionForm.get("sections") as FormArray;
  }

  stepsFormArray(sectionControl) {
    return sectionControl.get("inspectionSteps") as FormArray;
  }

  getAcks(step) {
    return step.get("acknowledgements") as FormArray;
  }

  getInputs(step) {
    return step.get("inputs") as FormArray;
  }

  nextSection(formGroup: FormGroup) {
    this.formUtils.updateValueAndValidity(formGroup);
    if (formGroup.invalid) {
      FormUtils.markFormGroup(this.inspectionForm);
      this.toastrService.error("errors found", null, {
        positionClass: "toast-top-center",
      });

      // reason for timeout is because it took angular some time to show error text. we can probably do this better
      setTimeout(() => {
        this.windowService.scrollToElement(".text-red");
      }, 300);

      return;
    }

    // set section to complete and set next section to inprogress
    const sections = this.inspectionForm.controls.sections as FormArray;
    const currentSection = sections.controls[this.currentSectionIndex] as FormGroup;
    currentSection.controls.internalNotes.patchValue("complete");
    currentSection.controls.complete.patchValue(true);
    this.currentSectionIndex++;

    // json patch current section
    let operation = [{ op: "replace", path: `/sections/${this.currentSectionIndex - 1}/internalNotes`, value: "complete" }];

    // json patch next section
    const nextSection = sections.controls[this.currentSectionIndex] as FormGroup;
    if (!nextSection.value.internalNotes?.includes("complete")) {
      nextSection.controls.internalNotes.patchValue("inProgress");
      operation.push({ op: "replace", path: `/sections/${this.currentSectionIndex}/internalNotes`, value: "inProgress" });
    }

    this.logService.info(this.constructor.name, `nextSection click: ${this.inspectionForm.value.sections[this.currentSectionIndex].title}`);
    this.inspectionApiService.jsonPatchWithPartitionKey(this.inspection.id, operation, this.inspection.partitionKey).subscribe(result => {});
    this.scrollUp();
  }

  previousSection(formGrou: FormGroup) {
    if (this.currentSectionIndex == 0) {
      this.currentSectionIndex = null;
      this.scrollUp();
      return;
    }

    this.currentSectionIndex--;
    this.scrollUp();
  }

  goToChecklist() {
    this.formUtils.updateValueAndValidity(this.inspectionForm);

    // set section to complete and set next section to inprogress
    const sections = this.inspectionForm.controls.sections as FormArray;
    const currentSection = sections.controls[this.currentSectionIndex] as FormGroup;
    let operation;
    if (!currentSection.valid) {
      currentSection.controls.internalNotes.patchValue("inProgress");
      currentSection.controls.inProgress.patchValue(true);
      currentSection.controls.complete.patchValue(false);
      operation = [{ op: "replace", path: currentSection.value.jsonPatchPath + "/internalNotes", value: "inProgress" }];
    } else {
      currentSection.controls.internalNotes.patchValue("complete");
      currentSection.controls.inProgress.patchValue(false);
      currentSection.controls.complete.patchValue(true);
      operation = [{ op: "replace", path: currentSection.value.jsonPatchPath + "/internalNotes", value: "complete" }];
    }

    this.inspectionApiService.jsonPatchWithPartitionKey(this.inspection.id, operation, this.inspection.partitionKey).subscribe(result => {});
    this.inspectionChange.emit(this.inspectionForm.value);

    this.currentSectionIndex = null;

    this.scrollUp();
  }

  goToSection(sectionToGoTo: number) {
    this.currentSectionIndex = sectionToGoTo;
    this.scrollUp();
  }

  scrollUp() {
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    }, 300);
  }

  get progressbarValue() {
    return (this.currentSectionIndex / this.inspectionDefinition.sections.length) * 100;
  }

  get percentComplete() {
    return this.inspectionDefinition.sections.filter(d => d.complete).length / this.inspectionDefinition.sections.length;
  }
}
